/**
 * Comprehensive Knowledge Base for MLC Proposal HQ
 * Source of truth for platform features, personas, workflows, stages, and step-by-step guides.
 */

export interface KnowledgeTopic {
  id: string;
  category: "overview" | "persona" | "workflow" | "module" | "howto" | "rules" | "faq";
  title: string;
  summary: string;
  content: string;
  keywords: string[];
  suggestedLinks?: Array<{ label: string; path: string }>;
}

export const KNOWLEDGE_TOPICS: KnowledgeTopic[] = [
  {
    id: "vision-and-purpose",
    category: "overview",
    title: "Executive Vision & Platform Purpose",
    summary:
      "Why MLC Proposal HQ exists: a force-multiplier reducing proposal turnaround by 70–80% while eliminating compliance risk.",
    keywords: ["what is", "about", "purpose", "why", "overview", "vision", "goal", "introduction"],
    content: `**MLC Proposal HQ** is an enterprise proposal automation and intelligence platform built specifically for Multilingual Connections (MLC).

It functions as a force-multiplier across the entire RFP lifecycle:
1. **Accelerates Draft Assembly**: Reduces first-draft time by 70–80% using historical winning proposal intelligence and grounded AI synthesis.
2. **Eliminates Non-Compliance**: Automated requirement shredding, compliance matrices, required forms rosters, and Proposal Writing Coach audits ensure zero missed requirements.
3. **Protects Margins & Capacity**: Objective Go/No-Go opportunity fit scoring and red-flag contract analysis prevent burning team capacity on low-yield bids.
4. **Structured Planning & Section Ownership**: Auto-schedules milestones, tracks RFP addenda scope shifts, assigns section ownership, balances writer/SME workload, and locks an immutable plan baseline via the Plan Freeze Gate.
5. **Elevates Win Rates**: An AI client evaluator ("Red-Teamer") stress-tests arguments and pinpoints weak or unsupported claims before client submission.
6. **Master Proposal Compiler & Dual Sign-Off Gate**: Assembles all proposal sections, matrices, and forms into a client-ready package with automated pre-flight validation and mandatory Bid Lead and Executive sign-offs.
7. **Post-Mortem Debrief, FOIA Tracking & Rebid Engine**: Analyzes bid outcomes, tracks FOIA public records requests, logs competitor scorecards, visualizes pipeline win/loss analytics, and initializes 1-click rebids for recurring contracts.
8. **Unified RFP Workspace & Header Quick Switcher**: Top-level workspace navigation, searchable workspace picker, and persistent header quick switcher with 1-click stage tabs and memory.
9. **Self-Healing Living Library**: Captures new answers from completed bids with client sanitization, learns from outcomes, and provides permanent deletion or safe retirement with dependency safeguards.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "Go to Pipeline", path: "/" },
      { label: "View Answer Library", path: "/library" },
    ],
  },
  {
    id: "stages-architecture-overview",
    category: "overview",
    title: "Proposal HQ Stages: 8-Stage Workspace & Pipeline Architecture",
    summary:
      "A complete overview of the 8 workspace development stages and the pipeline opportunity lifecycle.",
    keywords: [
      "stages",
      "lifecycle",
      "architecture",
      "stages overview",
      "all stages",
      "different stages",
      "pipeline stages",
      "workspace stages",
      "8 stages",
      "workspace tabs",
    ],
    content: `**Proposal HQ End-to-End Stage Architecture**

MLC Proposal HQ organizes every bid into two complementary lifecycle models:

### 1. The 8 Workspace Development Stages (Tabs)
Inside each opportunity workspace, proposal teams advance through 8 sequential stages:
- **Stage 1: Intake & Multi-Format Shredder** (\`intake\`): Upload PDF/DOCX RFPs to automatically extract discrete requirements, format rules, and required forms.
- **Stage 2: Strategic Assessment & Go/No-Go Decisioning** (\`assessment\`): Evaluate 4-pillar win probability fit scores (0–100%), run contract red-flag scans, and log formal Go/Caution/No-Go decisions.
- **Stage 3: Proposal Response Plan, Milestones & Section Ownership** (\`requirements\` / PlanTab): Auto-schedule response timelines with business-day offsets, log RFP addenda and scope shifts, organize requirements with category rollups and bulk actions, allocate section leads and workload capacity, verify required forms and format rules, review the 1-Page Executive Brief, and freeze the plan baseline before drafting.
- **Stage 4: Dynamic Writing Board & Sourced Library Matching** (\`drafting\`): Toggle between proposal Section Outline and flat views, monitor real-time section word budget progress meters, execute 1-click Batch Auto-Drafting across unanswered requirements, format content with rich Markdown Editor & live preview, route technical prompts inline to SMEs, and synthesize grounded responses with win-weighted library answers.
- **Stage 5: Proposal Writing Coach & Compliance Audit** (\`coach\`): Perform line-by-line compliance audits against RFP instructions, check word/page budgets, and stage 1-click fixes into the editor.
- **Stage 6: Evaluator Red Team (AI Procurement Simulation)** (\`redteam\`): Adversarial client procurement committee simulation producing a letter grade (A–F), weighted dimension scores, and pinpointing unsupported claims.
- **Stage 7: Final Submission Package, Compiler & Sign-Off Gate** (\`submit\`): Track submission package checklists, compile all proposal sections and requirements into a master document, execute automated pre-flight package validation, obtain mandatory Bid Lead and Executive sign-offs, log delivery confirmation details, and export to Google Docs, Word, Markdown, CSV, or JSON.
- **Stage 8: Post-Mortem Debrief, FOIA Tracking & Rebid Engine** (\`debrief\`): Record Won/Lost outcomes with contributing factor tags, track FOIA public records requests for winning competitor proposals and scorecards, clone multi-year rebids with historical intelligence, and review pipeline win/loss analytics.

### 2. Opportunity Pipeline Stages
Opportunities advance across status milestones:
- **Reviewing**: In initial intake, qualification, and shredding.
- **Qualified (Go)**: Approved to bid; response planning active.
- **Declined (No-Go)**: Documented decision not to bid.
- **Drafting**: Response plan frozen; content authoring active.
- **In Review**: Color-team reviews (Pink/Red/Gold), Coach audits, and Evaluator Red-Teamer.
- **Approved**: Final executive sign-off for packaging.
- **Submitted**: Delivered to the client procurement portal/email with delivery confirmation.
- **Won / Lost**: Final outcome logged with post-mortem debrief, FOIA tracking, library win harvesting, and optional rebid initialization.
- **Archived**: Canceled, expired, or ended early.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "Go to Pipeline", path: "/" },
      { label: "Settings & Presets", path: "/settings" },
    ],
  },
  {
    id: "personas-and-roles",
    category: "persona",
    title: "Stakeholder Personas & Value Matrix",
    summary:
      "How MLC Proposal HQ serves Proposal Managers, Writers, SMEs, Executives, and Contracts Leads across all 8 stages.",
    keywords: [
      "personas",
      "roles",
      "manager",
      "writer",
      "sme",
      "executive",
      "contracts",
      "who uses",
      "stakeholders",
    ],
    content: `MLC Proposal HQ is tailored for 5 primary personas:

- **Proposal / Bid Manager**:
  * *Pain Points*: Tracking unanswered questions, chasing SMEs, uncoordinated schedules, scope creep from late client addenda, unverified submission packaging.
  * *Key Features*: Automated Milestone Scheduling, RFP Addenda Log, Requirements Matrix Bulk Actions, Section Ownership & Workload Allocation, 1-Page Executive Brief, Plan Readiness Audit & Freeze Gate, Stage 7 Final Submission Package Checklist & Master Compiler, Bid Lead Sign-Off Gate, Stage 8 Post-Mortem Debrief & FOIA tracking, and Header RFP Quick Switcher.
- **Proposal Writer / Bid Editor**:
  * *Pain Points*: Unclear section assignments, hunting through old Word docs, voice inconsistency, repetitive copy-pasting.
  * *Key Features*: Direct RFP Workspace top-level navigation, Header Quick Switcher with 1-click stage tabs, Section Allocation Matrix with target word counts, Split-Screen Drafting Board, Sourced Library Matching, First-Draft AI Synthesis, Proposal Writing Coach, Master Proposal preview, and past win/loss debrief insights.
- **Subject Matter Expert (SME)**:
  * *Pain Points*: Reading 90-page RFPs to answer 3 questions; answering repetitive duplicate questions.
  * *Key Features*: Targeted Question Cards, Review Queue (/reviews), Section Reviewer assignments, Pre-Ask SME Duplicate Detection, direct opportunity access via Header Quick Switcher.
- **Executive / Decision Committee**:
  * *Pain Points*: Burning team capacity on low-yield bids; lack of visibility into proposal readiness, final submission sign-offs, and historical win/loss drivers.
  * *Key Features*: 4-Pillar Fit Scoring Engine, Red-Flag Contract Risk Scanner, 1-Page Executive Opportunity Brief, Team Workload Capacity Rollup, Plan Freeze sign-off, Stage 7 Executive Sign-Off Gate, Pipeline Win/Loss Analytics Modal, and Rebid Engine approval.
- **Contracts / Compliance Lead**:
  * *Pain Points*: Overlooking onerous terms, indemnification traps, missing mandatory certifications, unverified required forms and submission logistics.
  * *Key Features*: Red-Flag & Contract Risk Scanner, Required Forms Checklist with signatory tracking, Format & Submission Rules, Stage 7 Submission Package Validation (blocking pre-flight checks for mandatory compliance forms, unverified requirements, format rules, missing sign-offs), FOIA public records tracking, and Freshness Scanner.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "View Review Queue", path: "/reviews" },
      { label: "Staff Directory in Settings", path: "/settings/staff" },
    ],
  },
  {
    id: "module-1-intake-shredder",
    category: "module",
    title: "Stage 1 (Module 1): Intake & Multi-Format Shredder",
    summary:
      "Upload PDF and DOCX documents to automatically extract requirements, format constraints, and required forms.",
    keywords: [
      "shredder",
      "upload",
      "intake",
      "attachments",
      "pdf",
      "docx",
      "extract",
      "requirements",
      "extraction",
      "stage 1",
    ],
    content: `**Stage 1: Intake & Shredder Engine**
- **Supported Formats**: PDF and DOCX RFP documents.
- **Automated Breakdown**: Breaks 50–100+ page RFPs into tagged requirement line items (Technical, Pricing, Mandatory Qualifications, Submission Guidelines).
- **Constraint Detection**: Automatically isolates hidden submission constraints: font sizes, margins, page limits, delivery methods, and mandatory forms.
- **How to Use**:
  1. Go to the Pipeline (/) and click **"New Opportunity"** (or click into an existing opportunity).
  2. In the **"Intake & Attachments"** tab, drag and drop the client's RFP PDF/DOCX files.
  3. Click **"Run Automated Extraction"**. The AI extraction job processes in the background and populates the **"Requirements Breakdown"** in Stage 3.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "module-2-opportunity-assessment",
    category: "module",
    title: "Stage 2 (Module 2): Strategic Assessment & Go/No-Go Fit Scoring",
    summary:
      "Objective 0–100% win probability fit score, contract risk scanner, and 1-Page Executive Brief.",
    keywords: [
      "assessment",
      "fit score",
      "go no go",
      "risk",
      "red flag",
      "contract",
      "brief",
      "qualification",
      "probability",
      "stage 2",
    ],
    content: `**Stage 2: Opportunity Assessment & Decisioning**
- **4 Fit Scoring Pillars**:
  1. *Technical & Service Capability* (Translation, localization, transcreation, audio/video).
  2. *Domain & Industry Experience* (Healthcare, government, legal, education, etc.).
  3. *Resource Capacity & Turnaround* (Feasibility within deadline and estimated hours).
  4. *Library & Win Rate Depth* (Historical answers and past win rates in the sector).
- **Red-Flag & Contract Risk Scanner**:
  * Detects dangerous clauses: unlimited liability, aggressive liquidated damages, unfavorable IP transfer, short payment terms.
  * Identifies "wired" RFPs tailored suspiciously toward a competitor.
  * Flags missing mandatory qualifications (e.g. mandatory ISO 17100 or ISO 9001).
- **1-Page Executive Opportunity Brief**:
  * Auto-generates estimated effort hours, client pain points, recommended win theme, and risk mitigations.
  * Printable and exportable for leadership review.
- **Go / Caution / No-Go Decision Bar**:
  * Logs formal decisions with rationale directly into the opportunity stage history.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "stage-3-response-plan",
    category: "module",
    title: "Stage 3: Proposal Response Plan, Milestones & Section Ownership",
    summary:
      "Comprehensive proposal planning engine: response schedule, addenda log, requirements matrix, section allocation, forms checklist, format rules, executive brief, and plan freeze gate.",
    keywords: [
      "stage 3",
      "plan",
      "response plan",
      "milestones",
      "schedule",
      "addenda",
      "scope shift",
      "section ownership",
      "workload",
      "forms checklist",
      "format rules",
      "freeze",
      "plan freeze",
      "readiness",
      "brief",
      "export planning",
    ],
    content: `**Stage 3: Proposal Response Plan (PlanTab)**
Stage 3 is the operational nerve center that bridges opportunity qualification (Stage 2) and content authoring (Stage 4). It ensures that every proposal has a validated timeline, clear section ownership, balanced workloads, and an audit-approved frozen baseline.

### 7 Specialized Planning Sub-Tabs:
1. **Response Schedule & Milestones Timeline (\`schedule\`)**:
   - Auto-generates standard proposal milestones calculated backwards from the client submission deadline using business-day offsets configured in Settings.
   - Core milestones include: Kickoff, Shredding, Strategy Session, First Draft, Pink Team Review, Red Team Review, Gold Team Sign-Off, and Final Submission.
   - Tracks target vs. completion dates, assigned milestone owners, and statuses (\`pending\`, \`in_progress\`, \`completed\`, \`blocked\`, \`overdue\`).
2. **RFP Addenda & Scope Shift Log (\`addenda\`)**:
   - Tracks official client amendments, addenda numbers, issued dates, and impact levels (\`none\`, \`minor\`, \`moderate\`, \`major\`).
   - Documents scope shifts, logs deadline adjustments, and links affected requirement IDs so writers instantly see addenda impact flags.
3. **Requirements Breakdown & Category Rollups (\`requirements\`)**:
   - Tagged requirements categorized into Technical, Pricing, Mandatory Qualifications, Submission Guidelines, Legal/Compliance, and Company Background.
   - Category rollup progress bars display completion and verification percentages.
   - Multi-Select Bulk Actions: Bulk assign owners, bulk confirm/unconfirm, bulk re-categorize, and bulk delete.
4. **Bid Section Ownership & Workload Allocation (\`sections\`)**:
   - Groups requirements into standard proposal sections (Executive Summary, Technical Approach, Pricing, Experience/Case Studies, Quality & Security, Staffing).
   - Assigns a Section Lead, Contributing Writers, SME Reviewers, Target Word Counts, and Estimated Effort Hours.
   - Team Workload Rollup computes per-member assigned sections, total words, estimated hours, and capacity status.
   - Interactive filtering by team member lets writers and SMEs view their personalized workload.
5. **Required Forms Checklist (\`forms\`)**:
   - Documents mandatory vs. optional forms, required file formats, signatory mandates (Authorized Officer, Notary), assigned owners, and uploaded file linkages.
6. **Format & Submission Rules (\`rules\`)**:
   - Records document styling rules: margins, font family & size limits, line spacing, page count caps, delivery method (electronic portal, email, physical hard copies), and portal credentials/URLs.
7. **1-Page Executive Proposal Brief (\`brief\`)**:
   - Consolidated leadership brief synthesizing client background, opportunity win themes, milestone schedule, section ownership roster, required forms status, and risk assessment in a printable format.

### Quality & Governance Gates:
- **Plan Readiness Audit**: Automated 5-point audit evaluating milestones, confirmed requirements, section lead assignment, mandatory forms, and format rules. Produces a 0–100% Readiness Score with blocking errors vs. advisory warnings.
- **Plan Freeze Gate**: Once readiness criteria are met, Proposal Managers freeze the plan baseline (\`isPlanFrozen: true\`), locking the plan against accidental modifications before Stage 4 drafting starts.
- **Planning Package Export**: Export the entire proposal response plan to Formatted Google Docs/Sheets, Markdown bundle, CSV requirements matrix, or JSON data.`,
    suggestedLinks: [
      { label: "Open Pipeline", path: "/" },
      { label: "Configure Schedule Presets", path: "/settings" },
    ],
  },
  {
    id: "module-3-dynamic-writing",
    category: "module",
    title: "Stage 4 (Module 3): Stage 4 Drafting Engine Overhaul & Content Assembly",
    summary:
      "Section outline hierarchy, word budget meters, 1-click batch auto-drafting, rich markdown editor with live preview, and grounded library AI synthesis.",
    keywords: [
      "drafting",
      "writing",
      "editor",
      "markdown editor",
      "batch auto draft",
      "word budget",
      "word count",
      "section outline",
      "matching",
      "library answers",
      "generate",
      "ai draft",
      "stage 4",
      "sme routing",
      "ask sme",
    ],
    content: `**Stage 4: Proposal Authoring Studio & Drafting Engine Overhaul**
Stage 4 transforms shredded requirements into polished, cohesive narrative sections backed by verified institutional precedents:

### 1. Section Outline vs. Flat View
- **Structured Outline Mode**: Organizes requirements into standard proposal sections (Executive Summary, Technical Approach, Pricing, Experience/Case Studies, Quality & Security, Staffing) defined in Stage 3 Section Allocation.
- Displays Section Leads, assigned SME Reviewers, and requirement progress counters.
- **Flat View**: Direct chronological list of all requirements with category filters and search.

### 2. Section Word Budget Progress Meters
- Real-time word counter compares cumulative section text against target word budgets set in Stage 3.
- Color-coded threshold indicators prevent scope bloat:
  * **< 70%** (Cyan/Blue): Under budget / drafting in progress.
  * **70%–110%** (Emerald): Optimal target range.
  * **> 110%** (Rose): Over-budget warning indicating trimming is required.

### 3. Batch Auto-Draft Engine
- Click **"Batch Auto-Draft"** in the drafting header to open the batch drafting modal.
- Automatically audits all proposal requirements and identifies eligible unanswered questions with qualified library matches (≥ 30% similarity).
- Synthesizes grounded AI narrative sections sequentially with live progress and cancel controls, cutting initial draft authoring time by 70–80%.

### 4. Rich Markdown Editor & Live Preview
- Toggle seamlessly between the markdown source editor and rendered HTML preview.
- Complete formatting toolbar: Headings (H1–H3), Bold, Italic, Bullet Lists, Numbered Lists, Blockquotes, Markdown Tables, Inline/Block Code, and Hyperlinks.
- Live character and word counters with real-time budget compliance indicators.

### 5. Inline SME Delegation (Ask SME)
- Writers can click **"Ask SME"** directly on any drafting card to delegate complex questions to assigned Subject Matter Experts.
- Integrated duplicate detection checks the Answer Library first (≥ 50% match) to prevent redundant inquiries.

### 6. Sourced Library Matching & Win Boosts
- Compares requirements against the living Answer Library using semantic and keyword matching.
- **Win-Weighted Boosts**: Answers used in past winning proposals receive bonus score (+0.05 per win, cap +0.10) to prioritize high-converting arguments.
- **Strict Zero-Hallucination**: AI narrative is strictly grounded in verified library precedents and opportunity context.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "Answer Library", path: "/library" },
    ],
  },
  {
    id: "howto-batch-auto-draft",
    category: "howto",
    title: "How to Use Batch Auto-Drafting (Stage 4)",
    summary:
      "1-click automated AI drafting across all eligible unanswered requirements using verified library matches.",
    keywords: [
      "batch auto draft",
      "batch auto drafting",
      "batch auto-drafting",
      "batch drafting",
      "bulk drafting",
      "auto fill requirements",
      "batch auto-draft",
      "batch generate",
      "mass drafting",
    ],
    content: `**How to Use Batch Auto-Drafting in Stage 4:**
1. Navigate to your active proposal's **Drafting Board** tab (\`drafting\`) via the sidebar or Header RFP Quick Switcher.
2. Ensure you have loaded and reviewed your requirement shredding in Stage 3.
3. In the top header bar, click the **"Batch Auto-Draft"** button (with Sparkles icon).
4. The Batch Auto-Draft Modal displays an audit of all requirements:
   - Identifies requirements that are currently unanswered or incomplete.
   - Filters for those with qualifying library matches (confidence ≥ 30%).
   - Displays the top matched answer title and confidence score for each candidate.
5. Click **"Run Batch Auto-Draft"** to start the synthesis process.
6. The engine processes each requirement sequentially:
   - Displays live progress (e.g., "Drafting 3 of 12...").
   - Synthesizes tailored narrative grounded in the matched answer and client context.
   - Automatically marks the matched library answer as "Used in Draft" for win tracking.
7. Click **"Cancel"** at any time to pause after the current requirement finishes.`,
    suggestedLinks: [{ label: "Open RFP Workspace", path: "/workspace" }],
  },
  {
    id: "howto-word-budgets-and-editor",
    category: "howto",
    title: "How to Manage Section Word Budgets & Use the Markdown Editor (Stage 4)",
    summary:
      "Real-time target vs actual word tracking per proposal section, color-coded threshold meters, and formatting toolbar with live preview.",
    keywords: [
      "word budget",
      "word count",
      "target words",
      "section word budget",
      "markdown editor",
      "live preview",
      "formatting toolbar",
      "outline view",
    ],
    content: `**Managing Word Budgets & Using the Markdown Editor:**

### 1. Section Word Budget Tracking
- In Stage 3 (PlanTab -> Section Allocation), assign target word counts to each proposal section (e.g., Executive Summary: 750 words, Technical: 3,500 words).
- When writing in Stage 4 Drafting:
  * Switch to **Section Outline View** using the toggle at the top of the Drafting Board.
  * Each section header shows actual words written vs. target words (e.g., "1,240 / 1,500 words (83%)").
  * A color-coded progress meter alerts you to pacing:
    - **Cyan / Blue (< 70%)**: Drafting in progress.
    - **Emerald (70%–110%)**: Optimal length.
    - **Rose (> 110%)**: Over budget; requires editing before submission.

### 2. Using the Rich Markdown Editor & Live Preview
- Each requirement draft has an interactive Markdown editor.
- **Formatting Toolbar**: Quick-insert buttons for:
  * Headers: H1 (\`#\`), H2 (\`##\`), H3 (\`###\`)
  * Text styles: Bold (\`**text**\`), Italic (\`*text*\`)
  * Lists: Bulleted (\`- \`) and Numbered (\`1. \`)
  * Structure: Blockquotes (\`> \`), Markdown Tables, and Inline/Block Code
  * Links: Hyperlink helper dialogs
- **Edit vs. Preview Modes**: Click **"Preview"** to inspect rendered HTML with typography styling; click **"Edit"** to return to markdown source.
- Live character and word counters update on every keystroke.`,
    suggestedLinks: [{ label: "Open RFP Workspace", path: "/workspace" }],
  },
  {
    id: "howto-master-template-seed",
    category: "howto",
    title: "How to Seed the 2026 Master RFP Template & Use Drag-and-Drop Import",
    summary:
      "Hydrate 48 pre-verified boilerplate answers into the Answer Library and ingest past proposals via drag-and-drop.",
    keywords: [
      "master template",
      "seed template",
      "master rfp",
      "48 answers",
      "boilerplate answers",
      "import proposal",
      "drag and drop",
      "importer",
    ],
    content: `**Seeding the 2026 Master RFP Template & Drag-and-Drop Ingestion:**

### 1. 2026 Master RFP Template Quick-Seed (48 Answers)
- In the navigation bar, go to **Answer Library (/library)**.
- Click the **"Import Proposals"** button in the header.
- In the import dialog, click **"Seed 2026 Master RFP Template"**.
- This instantly populates 48 gold-standard, pre-verified boilerplate answers across 8 key operational domains:
  1. **Company Overview**: Founding, mission, executive leadership, and business history.
  2. **Certifications & Diversity**: Certified WBE (Women's Business Enterprise) credentials and state/local supplier diversity recognition.
  3. **Quality Management**: Three-tier TEP (Translation, Editing, Proofreading) quality assurance methodology and linguist qualification criteria.
  4. **Security & Data Privacy**: HIPAA, FERPA, GDPR, and SOC 2 data protection safeguards.
  5. **Translation & Localization**: Translation memory (TM), CAT tools, term bases, and neural MT post-editing workflows.
  6. **Interpreting Services**: On-site, over-the-phone (OPI), and video remote interpreting (VRI) capabilities in 75+ languages.
  7. **Audio/Video & Multimedia**: Transcription, voiceover, subtitling, and closed-captioning capabilities.
  8. **Disaster Recovery & Business Continuity**: Redundant cloud infrastructure, power backups, and emergency operational protocols.
- All seeded answers enter the library as pre-verified with tagged service areas and freshness dates.

### 2. Drag-and-Drop Proposal Importer
- In the same **"Import Proposals"** dialog, you can upload existing winning proposals or client RFPs.
- Simply drag and drop your PDF or DOCX file directly into the designated drop zone.
- The importer automatically parses the document, splits it into structured Q&A pairs, and stages candidate answers for your review.`,
    suggestedLinks: [{ label: "Go to Answer Library", path: "/library" }],
  },
  {
    id: "module-4-coach-and-red-team",
    category: "module",
    title: "Stage 5 & 6 (Module 4): Proposal Writing Coach & Evaluator Red-Teamer",
    summary:
      "Dual-tier validation: Proposal Coach compliance audits (Stage 5) and AI Client Red-Teamer adversarial scoring (Stage 6).",
    keywords: [
      "coach",
      "red team",
      "evaluator",
      "compliance",
      "grade",
      "audit",
      "adversarial",
      "unsupported claims",
      "freshness",
      "stage 5",
      "stage 6",
    ],
    content: `**Stage 5 & 6: Human-in-the-Loop Review, Compliance & Red-Teaming**
- **Proposal Writing Coach (Stage 5 — "Coach Review" Tab)**:
  * Line-by-line compliance audit comparing drafts against RFP requirements, formatting rules, and required forms.
  * Actionable suggestions categorized by severity (high, medium, low) with one-click staging into the editor.
- **AI Client Red-Teamer (Stage 6 — "Evaluator Red Team" Tab)**:
  * Simulates a skeptical client procurement committee.
  * Generates an overall Letter Grade (A/B/C/D/F) and weighted numerical score across Technical Excellence (35%), Compliance & Risk (25%), Price/Value (20%), and Past Performance (20%).
  * Pinpoints fluffy, unsupported claims (e.g., claiming "industry leader" without proof).
  * Evaluates addressing of implicit client pain points.
- **Pure Freshness Scanner**:
  * Detects outdated years (e.g., "In 2021..."), retired library citations, or departed team members.`,
    suggestedLinks: [{ label: "View Review Queue", path: "/reviews" }],
  },
  {
    id: "stage-6-final-submission",
    category: "module",
    title: "Stage 7: Final Submission Package, Compiler & Dual Sign-Off Gate",
    summary:
      "Final submission checklist, automated master proposal compiler, package validation audit, mandatory dual sign-offs (Bid Lead & Executive), and portal delivery tracking.",
    keywords: [
      "submission",
      "final submission",
      "submit",
      "compiler",
      "master compiler",
      "sign off",
      "sign-off",
      "lead sign off",
      "executive sign off",
      "package validation",
      "delivery confirmation",
      "portal submission",
      "stage 7",
      "submit tab",
    ],
    content: `**Stage 7: Final Submission Package, Compiler & Dual Sign-Off Gate (SubmitTab)**
Stage 7 is the final quality, assembly, and executive governance gate before delivery to the client procurement portal or agency contact.

### Core Capabilities:
1. **Submission Package Checklist**:
   - Tracks all deliverables: Master Proposal Narrative, Completed Pricing/Cost Schedules, Mandatory Compliance Forms & Affidavits, Client-Specific Attachments, and Portals/Credentials.
   - Real-time status toggles (\`pending\`, \`in_progress\`, \`ready\`) with progress meter.
2. **Automated Master Proposal Compiler**:
   - Merges approved draft sections, requirements compliance matrix, executive summary, company profile, and appendices into a unified, formatted document.
   - Real-time preview with live word count, page estimates, and section status indicators.
3. **Automated Package Validation Pre-Flight**:
   - Scans proposal before sign-off:
     * Unanswered or unverified mandatory requirements
     * Unchecked or unassigned required forms
     * Missing section leads or unapproved drafts
     * Format constraint violations (page caps, font rules)
   - Separates blocking red errors from advisory amber warnings.
4. **Mandatory Dual Sign-Off Gate**:
   - **Bid / Proposal Lead Sign-Off**: Confirms narrative completeness, compliance matrix verification, and format conformance.
   - **Executive Sign-Off**: Confirms pricing strategy, legal terms, commercial commitments, and executive authorization.
   - Both sign-offs record signer name, email, timestamp, and optional condition notes.
5. **Delivery Tracking & Confirmation Modal**:
   - Clicking **"Record Submission"** logs:
     * Confirmation / Tracking Number (from client portal or email receipt)
     * Exact Delivery Timestamp
     * Delivery Method (State/Federal Portal, Bonfire, IonWave, Jaggaer, Email, Physical Courier)
     * Recipient Contact / Submission URL
     * Submitting User identification & confirmation notes
   - Automatically transitions the opportunity to \`submitted\` stage.
6. **Multi-Format Export & Compilation Manifest**:
   - One-click exports to **Google Docs** (rich formatted clipboard copy), **Microsoft Word**, **Markdown Bundle** (\`.md\`), **CSV Package Checklist**, and **JSON Audit Manifest**.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "View Pipeline", path: "/" },
    ],
  },
  {
    id: "stage-7-debrief-foia-rebid",
    category: "module",
    title: "Stage 8: Post-Mortem Debrief, FOIA Scorecards & Rebid Engine",
    summary:
      "Structured post-mortem debrief, FOIA public records tracking, competitor evaluation scorecards, win/loss analytics, and 1-click rebid cloning.",
    keywords: [
      "debrief",
      "post mortem",
      "post-mortem",
      "foia",
      "public records",
      "rebid",
      "rebid engine",
      "win loss analytics",
      "competitor scorecard",
      "lessons learned",
      "outcome",
      "won lost",
      "stage 8",
      "debrief tab",
    ],
    content: `**Stage 8: Post-Mortem Debrief, FOIA Scorecards & Rebid Engine (DebriefTab)**
Stage 8 closes the continuous improvement loop, turning every bid outcome into proprietary competitive advantage for future opportunities.

### Core Capabilities:
1. **Comprehensive Post-Mortem Debrief**:
   - Captures formal outcome: **Won 🏆**, **Lost**, **Disqualified**, or **Withdrawn**.
   - Contributing factor attribution tags: Pricing, Technical Approach, Domain Experience, Turnaround Time, Compliance/Forms, Incumbent Advantage, Agency Relationship, Scoring Rubric Bias.
   - Verbatim client debrief quotes and internal lessons learned.
2. **FOIA & Public Records Request Tracking**:
   - Specialized workflow for public sector & government proposals:
     * Request Status: \`not_requested\`, \`requested\`, \`acknowledged\`, \`records_received\`, \`denied\`, \`appealed\`.
     * Logs request submission date, FOIA tracking number, FOIA officer contact, and responsive documents received.
     * Records access to winning competitor proposals and evaluation committee score sheets.
3. **Competitor & Evaluation Rubric Scorecard**:
   - Documents the winning vendor/competitor name, winning bid price, and price variance vs MLC.
   - Breaks down rubric scores across technical, past performance, pricing, and interview rounds.
   - Highlights client-noted MLC strengths vs competitor differentiators.
4. **1-Click Rebid Engine (\`rebidService\`)**:
   - For multi-year contract expirations or anticipated recompetes:
     * Clones the opportunity into a new rebid cycle.
     * Links back to the original bid history and preserves contract renewal dates.
     * Automatically pre-populates past lessons learned, client preferences, competitor scorecards, and winning answers into the new workspace.
5. **Executive Win/Loss Analytics Modal**:
   - Accessible directly on the Pipeline view via **"Win/Loss Analytics"**:
     * Aggregate win rate percentage across decided proposals.
     * Filterable breakdown by Service Category (Translation, Audio/Video, Localization, Interpretation).
     * Breakdown by Client Sector / Industry and Contract Value Brackets.
     * Root-cause Pareto chart of winning and losing factors.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "Open Pipeline", path: "/" },
    ],
  },
  {
    id: "navigation-workspace-and-switcher",
    category: "workflow",
    title: "Unified RFP Workspace, Workspace Picker & Header Quick Switcher",
    summary:
      "Primary top-level navigation, searchable Workspace Picker with stage filtering, and persistent Header Quick Switcher with 1-click stage tab jumps and memory.",
    keywords: [
      "workspace",
      "workspace picker",
      "quick switcher",
      "header switcher",
      "switcher",
      "active opportunity",
      "navigation",
      "rfp switcher",
      "stage jump",
      "recent rfps",
      "switch rfp",
    ],
    content: `**Unified RFP Workspace Navigation & Header Quick Switcher**
Proposal HQ elevates the Opportunity Workspace into a primary, first-class top-level navigation destination with intelligent context switching and memory.

### Key Capabilities:
1. **Primary Sidebar Navigation**:
   - **Workspace** (\`/workspace\`) is located directly in the left navigation sidebar alongside Pipeline, Answer Library, Review Queue, and Settings.
   - If an active RFP is currently selected, clicking **Workspace** immediately resumes your work inside that opportunity without losing your place.
2. **Interactive Workspace Picker View (\`WorkspacePickerView\`)**:
   - Displayed when navigating to \`/workspace\` without an active RFP selected, or when clicking **"Switch RFP"** / **"Browse All Opportunities"**.
   - **Stage Filter Tabs**: All Opportunities, Active RFPs (In Review / Drafting / Qualified), Submitted, and Won / Decided.
   - **Quick Search**: Instant live filtering by RFP title, client name, industry, or contract type.
   - **Summary KPI Badges**: Active RFPs count, Proposals Due This Week, Total Pipeline Value, and Average Fit Score.
   - **Opportunity Cards**: Rich cards showing client name, stage badge, submission due date countdown, requirement progress bar, and 1-click **"Open Workspace"** action.
3. **Persistent Header RFP Quick Switcher (\`HeaderRfpSwitcher\`)**:
   - Located at the top of every screen in Proposal HQ.
   - **Active Opportunity Pill**: Displays the active RFP title, client, stage badge, and days remaining until deadline.
   - **1-Click Stage Tab Jumps**: Dropdown provides direct shortcut pills to jump straight to **Intake**, **Assessment**, **Plan**, **Drafting**, **Coach**, **Red Team**, **Submit**, or **Debrief**.
   - **Recent Opportunities**: Instant 1-click switching to your most recently accessed proposals.
   - **Quick Search Filter**: Type any keyword to find any RFP in your workspace instantly without leaving your current page.
4. **Automatic Memory & Cross-Tab Sync (\`activeOpportunityService\`)**:
   - Proposal HQ remembers your active RFP in localStorage.
   - Navigating between the Pipeline, Library, Review Queue, and Settings preserves your active RFP context.
   - Multi-tab synchronization updates your active RFP across browser tabs seamlessly.`,
    suggestedLinks: [
      { label: "Go to RFP Workspace", path: "/workspace" },
      { label: "Go to Pipeline", path: "/" },
    ],
  },
  {
    id: "library-deletion-and-retirement",
    category: "howto",
    title: "Answer Library: Permanent Deletion vs Safe Retirement",
    summary:
      "Manage answer lifecycles safely: retire outdated answers to preserve historical win citations, or permanently delete with active proposal dependency safeguards.",
    keywords: [
      "delete answer",
      "permanent delete",
      "retire answer",
      "safe retirement",
      "unretire",
      "library deletion",
      "answer lifecycle",
      "delete modal",
    ],
    content: `**Managing Answer Lifecycle: Deletion vs Safe Retirement**
To protect the integrity of historical proposals, win rate metrics, and active drafts, Proposal HQ provides a two-tier governance model for retiring or deleting answers in the Answer Library (\`/library\`).

### 1. Safe Retirement (Recommended for Past Winning / Cited Content):
- **When to Use**: The answer contains outdated messaging, superseded pricing, or retired service descriptions, but was used in past proposals.
- **How to Retire**:
  1. Open the answer in the **Answer Library** or **Review Queue**.
  2. Click **"Retire Answer"** (or select **"Retire"** in the action menu).
  3. Enter an optional reason (e.g., "Updated in 2026 for new pricing structure").
- **What Happens**:
  - The answer status changes to \`retired\`.
  - It is hidden from standard library search and excluded from automated AI draft matching.
  - Historical proposal citations, draft references, and win credit attributions (**🏆 Won in N proposals**) remain completely intact.
  - You can reactivate the answer at any time by clicking **"Un-Retire"**.

### 2. Permanent Deletion (For Erroneous, Duplicate, or Test Entries):
- **When to Use**: The answer was created by mistake, is a duplicated scrap entry, or contains erroneous content that should never exist.
- **How to Permanently Delete**:
  1. Open the answer and click **"Delete Answer Permanently"** (trash icon).
  2. The **Delete Answer Safeguard Modal** appears.
  3. **Dependency Check**: If the answer is actively cited in existing proposals or drafts, Proposal HQ displays a warning list of affected proposals and recommends Safe Retirement instead.
  4. To proceed, type the word **"DELETE"** or confirm the warning checklist, then click **"Permanently Delete"**.
  5. The Firestore document is completely deleted and removed from all indexes.`,
    suggestedLinks: [{ label: "Go to Answer Library", path: "/library" }],
  },
  {
    id: "module-5-living-library-feedback",
    category: "module",
    title: "Living Library Harvesting, Win Credit & Knowledge Feedback",
    summary:
      "Harvest new answers from completed bids with client sanitization, attribute win credit to high-converting responses, and renew review cadences.",
    keywords: [
      "living library",
      "harvest",
      "win credit",
      "sanitize",
      "sanitize client",
      "review due",
      "library feedback",
      "harvest to library",
    ],
    content: `**Living Library Harvesting & Knowledge Feedback Loop**
Proposal HQ is a self-enriching knowledge engine: every proposal written trains and strengthens the central Answer Library.

- **Win Credit Attribution**:
  * When an opportunity is marked **Won 🏆**, every library answer tagged as "Used in Draft" automatically receives win credit.
  * Answers display **🏆 Won in N proposals** badges, and receive an algorithmic confidence boost (+0.05 per win, cap +0.10) in Stage 4 AI matching.
- **1-Click "Harvest to Library"**:
  * In the Drafting Board or Debrief Tab, click **"Harvest to Library"**.
  * The automated shredder isolates custom answers crafted during the bid.
  * **Entity Sanitization (\`sanitizeClient\`)**: Prospective client names, agency project codes, and proprietary financial numbers are redacted or replaced with generic placeholders.
  * Staged answers land in the Answer Library with status \`review_due\` for SME approval before entering production rotation.
- **Governance & Freshness**:
  * Verification cadences (e.g. 90-day review cycles) ensure content remains accurate.
  * The **Pure Freshness Scanner** flags stale years, departed staff owners, and retired citations.`,
    suggestedLinks: [{ label: "Go to Answer Library", path: "/library" }],
  },
  {
    id: "howto-response-schedule",
    category: "howto",
    title: "How to Auto-Generate Proposal Schedules & Milestones",
    summary:
      "Generate custom milestone timelines based on client due dates and business-day offset settings.",
    keywords: [
      "schedule",
      "milestone",
      "timeline",
      "offsets",
      "business days",
      "auto-generate schedule",
      "how to schedule",
    ],
    content: `**Auto-Generating Response Schedules in Stage 3**
1. Open the opportunity in **Opportunity Workspace** and select the **"Response Plan"** tab.
2. Click the **"Milestones & Schedule"** sub-tab.
3. Click **"Auto-Generate Schedule"**:
   - The system checks the opportunity submission due date and your workspace timezone.
   - Calculates working-day milestone offsets (configured in **Settings -> Proposal HQ AI & Automation**):
     * SME Inputs Due (default: -5 business days)
     * First Draft Complete (default: -4 business days)
     * Red Team Review (default: -2 business days)
     * Final Sign-off / Freeze (default: -1 business day)
   - Automatically skips weekends so all target deadlines land on valid working days.
4. You can edit dates, add custom milestones (e.g. Kickoff, Legal Review), or reassign milestone owners at any time.`,
    suggestedLinks: [
      { label: "Configure Schedule Settings", path: "/settings" },
      { label: "Go to Pipeline", path: "/" },
    ],
  },
  {
    id: "howto-rfp-addenda",
    category: "howto",
    title: "How to Track RFP Addenda & Scope Changes",
    summary:
      "Log client RFP amendments, scope shifts, deadline extensions, and identify affected requirements.",
    keywords: [
      "addenda",
      "addendum",
      "amendment",
      "scope change",
      "scope shift",
      "extension",
      "affected requirements",
    ],
    content: `**Tracking RFP Addenda in Stage 3**
1. In the **"Response Plan"** tab, navigate to the **"RFP Addenda Log"** sub-tab.
2. Click **"Log New Addendum"**:
   - Enter the official **Addendum Number** (e.g., "Addendum 01", "Q&A Clarification #2").
   - Set the **Issued Date** and select an **Impact Level** (*None*, *Minor*, *Moderate*, *Major Scope Shift*).
   - Enter a concise summary of what changed (scope reductions, additions, revised terms).
   - If the client extended the due date, enter the **New Due Date** to automatically update the opportunity.
   - Select affected requirements from the checklist to flag them visually in the matrix.
3. Affected requirements display an amber **Addendum Impact** badge in the Requirements Matrix so writers can revise accordingly.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "howto-requirements-bulk",
    category: "howto",
    title: "How to Use Requirements Matrix Bulk Actions & Category Rollups",
    summary:
      "Filter shredded requirements, check category completion rollups, and perform bulk assignments and status updates.",
    keywords: [
      "requirements matrix",
      "bulk actions",
      "bulk assign",
      "category rollup",
      "bulk confirm",
      "filter requirements",
    ],
    content: `**Managing Requirements Matrix & Bulk Operations in Stage 3**
1. Under **"Response Plan"**, click the **"Requirements Matrix"** sub-tab.
2. **Category Rollup Bar**:
   - Displays progress meters for Technical, Pricing, Mandatory Qualifications, Submission Guidelines, Legal/Compliance, and Company Background.
3. **Filtering & Searching**:
   - Filter by category, mandatory status, assignee, addendum impact, or keyword search.
4. **Multi-Select Bulk Actions**:
   - Check the select box on individual requirement cards or click **"Select All Filtered"**.
   - Use the floating action bar to:
     * **Assign Owner**: Assign a team member to all selected requirements simultaneously.
     * **Confirm / Unconfirm**: Bulk mark requirements as verified.
     * **Change Category**: Reclassify misidentified requirements.
     * **Delete Selected**: Safely remove non-applicable or duplicate requirements.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "howto-section-ownership",
    category: "howto",
    title: "How to Assign Section Ownership & Balance Team Workload",
    summary:
      "Organize requirements into proposal sections, assign leads and reviewers, and audit team capacity.",
    keywords: [
      "section ownership",
      "workload",
      "allocation",
      "capacity",
      "section lead",
      "writer assignment",
      "word count",
    ],
    content: `**Assigning Proposal Section Ownership & Workload in Stage 3**
1. In the **"Response Plan"** tab, click the **"Section Ownership & Workload"** sub-tab.
2. **Section Allocation Matrix**:
   - Requirements are organized into standard proposal sections: Executive Summary, Technical Approach, Pricing, Experience/Case Studies, Quality & Security, and Staffing.
   - Click **"Edit Section"** on any group to assign:
     * **Section Lead** (responsible for final narrative sign-off)
     * **Contributing Writers** (staff drafting subsections)
     * **SME Reviewers** (experts reviewing technical facts)
     * **Target Word Count** (budgeted length)
     * **Estimated Effort Hours** (time commitment)
3. **Team Workload Rollup**:
   - Summarizes total sections, target words, and estimated hours per team member.
   - Flags overloaded members with visual capacity warnings.
   - Click on any team member's avatar or name to filter the view to only their assigned sections.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "howto-plan-readiness-freeze",
    category: "howto",
    title: "How to Audit Plan Readiness & Freeze the Response Plan",
    summary:
      "Run the 5-point readiness audit and lock the plan baseline before beginning Stage 4 drafting.",
    keywords: [
      "freeze plan",
      "readiness",
      "audit",
      "plan readiness",
      "blocking errors",
      "gate",
      "lock plan",
    ],
    content: `**Plan Readiness Audit & Plan Freeze Gate in Stage 3**
Before authoring narrative in Stage 4, Proposal HQ enforces a structured governance gate to guarantee readiness.

1. In the **"Response Plan"** tab, look at the action header and click **"Plan Readiness Audit"** (or click the status banner).
2. The **Plan Readiness Modal** evaluates 5 criteria:
   - **Milestones**: All critical color-team reviews and submission milestones are scheduled before the deadline.
   - **Requirements**: Shredded requirements have been reviewed and verified.
   - **Section Ownership**: Every section has an assigned Section Lead.
   - **Required Forms**: Mandatory compliance forms are accounted for and assigned.
   - **Format Rules**: Font sizes, page caps, and delivery instructions are documented.
3. Review the **Readiness Score (0–100%)**:
   - *Blocking Errors* (red) must be resolved before the plan can be frozen.
   - *Advisory Warnings* (amber) provide recommendations but do not prevent freezing.
4. Click **"Freeze Response Plan"**:
   - Locks the planning baseline, sets \`isPlanFrozen: true\`, records the freeze timestamp, and activates Stage 4 Drafting with an immutable scope.
   - If emergency revisions are required later, managers can unfreeze the plan with documented rationale.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "howto-export-planning-package",
    category: "howto",
    title: "How to Export the Planning Package & Executive Brief",
    summary:
      "Export the complete response plan, requirements matrix, and executive brief in Docs, Markdown, CSV, or JSON.",
    keywords: [
      "export plan",
      "planning package",
      "export planning",
      "csv matrix",
      "google docs",
      "markdown plan",
      "json export",
    ],
    content: `**Exporting the Proposal Response Planning Package**
1. In the **"Response Plan"** tab, click **"Export Planning Package"** in the top action bar.
2. Select your desired export format:
   - **Copy Formatted (Google Docs / Word)**: Copies rich HTML with formatted tables, badges, and headers directly to your clipboard for instant pasting.
   - **Download Markdown (\`.md\`)**: Full structured text bundle including opportunity metadata, schedule, addenda log, section ownership roster, and requirements matrix.
   - **Download CSV Matrix**: Tabular export of all requirements, categories, compliance status, assigned owners, and notes for Excel or Google Sheets.
   - **Download JSON**: Complete raw data payload for enterprise archival or custom reporting.
3. You can also view the **"Executive Brief"** sub-tab to preview and print a clean 1-page summary.`,
    suggestedLinks: [{ label: "View Pipeline", path: "/" }],
  },
  {
    id: "sme-workflow-and-review-queue",
    category: "workflow",
    title: "Subject Matter Expert (SME) Workflow & Review Queue",
    summary:
      "How human Subject Matter Experts (SMEs) are designated, questions are routed without email clutter, and tasks are resolved in the Review Queue.",
    keywords: [
      "sme",
      "smes",
      "subject matter expert",
      "subject matter experts",
      "sme workflow",
      "how smes work",
      "how sme works",
      "who is an sme",
      "is sme ai",
      "human staff",
      "routing",
      "assign",
      "designate sme",
      "review queue",
      "ask sme",
      "duplicate",
      "tasks",
      "assigned to me",
      "email",
      "notifications",
    ],
    content: `**Subject Matter Expert (SME) Workflow & Review Queue**

### 1. Human Staff vs. AI Agent
* **SMEs are real human staff members**, NOT AI agents.
* The AI handles automated tasks (Intake shredding, Batch Auto-Drafting, Coach compliance audits, and Evaluator Red-Teaming).
* Human SMEs (such as your Translation Leads, Interpreting Managers, Audio/Video Leads, IT/Security Leads, or Operations staff) provide domain expertise for questions that require specific institutional or technical knowledge.

### 2. Designating SMEs in Your Staff
* **Synced Staff Directory**: Your company staff directory is synced automatically from MLC Staff HQ (\`mlc-hr-tools-db\`). All active \`@mlconnections.com\` users appear in the directory.
* **Stage 3 Section Allocation**: During proposal planning in Stage 3 (\`sections\` sub-tab), Proposal Managers assign **SME Reviewers** to specific proposal sections (e.g. assigning the Interpreting Manager to the Interpreting Services section).

### 3. How Questions Are Routed ("Ask SME")
* In Stage 4 Drafting Board, writers click the **"Ask SME"** button directly on any requirement card that needs specialist input.
* **Pre-Ask Duplicate Detection**: Proposal HQ automatically checks the Answer Library first (≥ 50% match). If a verified answer already exists, it alerts the writer to avoid bothering busy SMEs with redundant questions.
* If fresh input is needed, the writer selects the designated SME, enters their specific question, sets a due date, and submits.

### 4. How SMEs Work (No Email Spam / In-App Review Queue)
* **Does it email them?** No, Proposal HQ does **not** flood staff inboxes with automated emails.
* **Targeted In-App Review Queue (\`/reviews\`)**: All questions route directly to the SME's **Review Queue** under the **"Assigned to Me"** tab.
* SMEs log in and see bite-sized question cards with full context. They **never have to read a 90-page RFP document** just to answer a couple of questions.
* **Resolution**: The SME types their answer and clicks **"Submit to Proposal Team"**. The proposal writer can then review, accept it into the draft, or return it for revision with notes.`,
    suggestedLinks: [
      { label: "Go to Review Queue", path: "/reviews" },
      { label: "View Staff Directory", path: "/settings" },
      { label: "Go to RFP Workspace", path: "/workspace" },
    ],
  },
  {
    id: "google-docs-export",
    category: "howto",
    title: "How to Export Finished Proposals to Google Docs or Word",
    summary: "Exporting formatted proposals with preserved styling or downloading clean Markdown.",
    keywords: [
      "export",
      "google docs",
      "word",
      "download",
      "copy",
      "format",
      "clipboard",
      "markdown",
    ],
    content: `**Exporting Finished Proposals**
1. Open the proposal in **Opportunity Workspace**.
2. Navigate to the **"Coach Review"** tab or look for the export button in the header.
3. Click **"Copy Formatted (Google Docs)"**:
   * Copies formatted rich text directly to your clipboard.
   * Paste with **Ctrl+V** (or **Cmd+V**) into Google Docs or Microsoft Word.
   * Headings (H1, H2, H3), bold text, bulleted lists, and tables are preserved perfectly.
4. Click **"Download Markdown"** to save a \`.md\` file for local archiving.
*Note: Any unapproved sections or unresolved [ACTION NEEDED] placeholders will display a warning banner.*`,
    suggestedLinks: [{ label: "Pipeline", path: "/" }],
  },
  {
    id: "company-facts-and-rules",
    category: "rules",
    title: "MLC Company Profile & Honest Claims Policy",
    summary:
      "Mandatory corporate guidelines: Certified WBE translation company; never claim ISO certifications.",
    keywords: [
      "iso",
      "certification",
      "wbe",
      "company",
      "multilingual connections",
      "facts",
      "jill",
      "rules",
      "claims",
    ],
    content: `**Multilingual Connections (MLC) Profile & Mandatory Rules**
- **Company Profile**:
  * Certified Women's Business Enterprise (WBE), founded by Jill Bishop.
  * Full-service translation, localization, transcreation, multimedia (subtitling/voiceover/transcription), and research support in 75+ languages.
- **STRICT ZERO-HALLUCINATION / HONEST PROFILE POLICY**:
  * Multilingual Connections is **NOT** ISO 9001 certified and **NOT** ISO 17100 certified.
  * **NEVER** claim ISO certifications in drafts or library answers.
  * If an RFP mandates ISO certifications, Proposal HQ automatically flags it as a high/critical qualification red flag in Go/No-Go assessment.`,
    suggestedLinks: [{ label: "General & AI Settings", path: "/settings" }],
  },
];

const STOP_WORDS = new Set([
  "how",
  "what",
  "when",
  "where",
  "why",
  "who",
  "can",
  "you",
  "the",
  "and",
  "for",
  "with",
  "this",
  "that",
  "does",
  "are",
]);

/**
 * Client-side semantic & keyword matching engine
 * Provides immediate, accurate answers even when offline or during network issues.
 */
export function matchKnowledgeQuery(query: string): {
  reply: string;
  matchedTopic?: KnowledgeTopic;
  suggestedQuestions: string[];
  suggestedLinks?: Array<{ label: string; path: string }>;
} {
  const lower = query.toLowerCase().trim();
  // Strip punctuation so words like "SME's", "work?", "clear." are cleanly isolated
  const cleanQuery = lower.replace(/[.,?!:;"'()/[\]{}*_`~]/g, " ");
  const rawWords = cleanQuery.split(/\s+/).filter((w) => w.length > 2);

  const words: string[] = [];
  const seenWords = new Set<string>();
  for (const w of rawWords) {
    if (!seenWords.has(w)) {
      seenWords.add(w);
      words.push(w);
    }
    // Expand plural words to singular (e.g. smes -> sme)
    if (w.endsWith("s") && w.length > 3) {
      const singular = w.slice(0, -1);
      if (!seenWords.has(singular)) {
        seenWords.add(singular);
        words.push(singular);
      }
    }
  }

  let bestMatch: KnowledgeTopic | null = null;
  let bestScore = 0;

  for (const topic of KNOWLEDGE_TOPICS) {
    let score = 0;
    const titleLower = topic.title.toLowerCase();

    // Check title match
    if (lower === titleLower) {
      score += 30;
    } else if (titleLower.includes(lower)) {
      score += 20;
    }

    // Check keyword matches
    for (const kw of topic.keywords) {
      const kwLower = kw.toLowerCase();
      const kwClean = kwLower.replace(/[.,?!:;"'()/[\]{}*_`~-]/g, " ").replace(/\s+/g, " ");
      if (lower === kwLower || cleanQuery === kwClean) {
        score += 25;
      } else if (lower.includes(kwLower) || cleanQuery.includes(kwClean)) {
        score += kwLower.includes(" ") || kwLower.includes("-") ? 14 : 8;
      }
    }

    // Check query word overlap with title and keywords (counted once per query word)
    const titleWords = titleLower.split(/[\s,.:()&/-]+/).filter((w) => w.length > 2);
    const kwText = topic.keywords.join(" ").toLowerCase();
    const contentLower = topic.content.toLowerCase();

    for (const word of words) {
      const isStop = STOP_WORDS.has(word);
      if (titleWords.includes(word)) {
        score += isStop ? 1 : 6;
      } else if (kwText.includes(word)) {
        score += isStop ? 0.5 : 3;
      }
      if (contentLower.includes(word)) {
        score += isStop ? 0.2 : 1;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = topic;
    }
  }

  // Fallback default suggestions including Stage 3, Stage 4 drafting overhaul, Stage 7 submission, Stage 8 debrief, and workspace navigation
  const defaultQuestions = [
    "How do I run Batch Auto-Drafting across all unanswered requirements in Stage 4?",
    "How do Section Word Budgets and the Markdown Editor work in Stage 4?",
    "How do I seed the 48-answer 2026 Master RFP Template into the library?",
    "How does the Stage 7 Final Submission package and sign-off gate work?",
    "How do I track FOIA public records and launch a rebid in Stage 8?",
    "How do I use the RFP Workspace and Header Quick Switcher?",
    "How do I build a Proposal Response Plan in Stage 3?",
    "How does the Plan Readiness Audit and Freeze Gate work?",
    "How do I assign section ownership and balance workload?",
    "How do I track RFP addenda and scope shifts?",
    "How does Go/No-Go fit scoring work?",
    "What does the Evaluator Red Team check?",
    "How do I retire or permanently delete an answer from the library?",
    "How do I export to Google Docs?",
  ];

  if (!bestMatch || bestScore < 4) {
    return {
      reply: `I can help you with anything related to **MLC Proposal HQ**!\n\nHere are some popular topics across our 8 workspace stages and platform tools:\n- **Stage 1 (Intake & Shredder)**: Uploading RFPs and extracting requirements\n- **Stage 2 (Strategic Assessment)**: Go/No-Go fit scoring and contract risk scanning\n- **Stage 3 (Response Plan)**: Milestone schedules, addenda log, section allocation, and plan freeze gate\n- **Stage 4 (Dynamic Writing Overhaul)**: Section outline hierarchy, word budget meters, 1-click batch auto-drafting, and rich markdown editor with live preview\n- **Stage 5 & 6 (Coach & Red Team)**: Compliance audits and client procurement simulation\n- **Stage 7 (Final Submission)**: Master proposal compiler, package checklist, and dual sign-off gate\n- **Stage 8 (Debrief & Rebid)**: Post-mortem debrief, FOIA tracking, win/loss analytics, and rebid cloning\n- **Answer Library & Master Template**: 48 pre-verified 2026 boilerplate answers seed, drag-and-drop importer, and retirement vs deletion\n- **Platform Navigation**: Top-level RFP Workspace, Workspace Picker, and Header Quick Switcher\n\nTry clicking one of the suggested questions below or ask me a specific question!`,
      suggestedQuestions: defaultQuestions,
      suggestedLinks: [
        { label: "Go to RFP Workspace", path: "/workspace" },
        { label: "Go to Pipeline", path: "/" },
        { label: "Go to Answer Library", path: "/library" },
        { label: "Go to Review Queue", path: "/reviews" },
        { label: "Settings & Presets", path: "/settings" },
      ],
    };
  }

  return {
    reply: bestMatch.content,
    matchedTopic: bestMatch,
    suggestedQuestions: defaultQuestions.filter(
      (q) => !q.toLowerCase().includes(bestMatch?.title.toLowerCase().slice(0, 5) || ""),
    ),
    suggestedLinks: bestMatch.suggestedLinks,
  };
}
